<template>
    <div id="kitchen-settings"
         class="modl-tab-body">
        <div class="modl-body">
            <div v-if="error"
                 class="alert alert-danger">
                {{ error }}
            </div>

            <div v-if="loading"
                 class="text-center">
                <PageLoadingSpinner />
            </div>
            <div v-else>
                <form class="form-h">
                    <div class="form-group">
                        <label class="control-label">{{ l('KITCHEN_SETTINGS.PRINT_RECEIPTS__LABEL') }}</label>
                        <div class="form-h__right">
                            <div class="form-group__input">
                                <div class="form-group__checkbox">
                                    <input :checked="currentDevice.autoPrintKitchenReceipts"
                                           type="checkbox"
                                           disabled />
                                </div>
                            </div>
                            <p class="help-block">
                                {{
                                    l(
                                        currentDevice.autoPrintKitchenReceipts
                                            ? 'KITCHEN_SETTINGS.PRINT_RECEIPTS__HELP--ON'
                                            : 'KITCHEN_SETTINGS.PRINT_RECEIPTS__HELP--OFF'
                                    )
                                }}
                                <br />
                                {{ l('KITCHEN_SETTINGS.PRINT_RECEIPTS__HELP-EXTRA') }}
                            </p>
                        </div>
                    </div>
                </form>

                <template v-if="currentDevice.autoPrintKitchenReceipts">
                    <!-- Use a separate element for margin because .help-block breaks the mt-1 -->
                    <div class="mt-1"></div>
                    <p class="help-block text-center">
                        {{ l('KITCHEN_SETTINGS.PRINTERS__HELP') }}
                    </p>
                    <div class="mt-1"></div>

                    <table v-if="kitchenZones"
                           class="table table-striped table--valign-middle">
                        <thead>
                            <tr>
                                <th class="text-right">
                                    {{ l('KITCHEN_SETTINGS.PRINTERS_ZONE__LABEL') }}
                                </th>
                                <th>
                                    {{ l('KITCHEN_SETTINGS.PRINTERS_PRINTER__LABEL') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <KitchenZonePrintersRow v-for="kitchenZone in kitchenZones"
                                                    :key="kitchenZone.ID"
                                                    v-model="newPrintConfig.kitchenZones[kitchenZone.ID]"
                                                    :kitchen-zone="kitchenZone"
                                                    :printer-names="printerNames" />
                        </tbody>
                    </table>
                </template>
            </div>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <button class="btn btn-lg btn-default cancel-btn"
                    @click.prevent="$emit('cancel')">
                {{ l('CANCEL') }}
            </button>

            <!-- OK button -->
            <button class="btn btn-lg btn-primary"
                    :disabled="saving"
                    @click.prevent="submit">
                <i v-if="saving"
                   class="fa-regular fa-spinner fa-spin" />
                <template v-else>
                    {{ l('SAVE') }}
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import KitchenZonePrintersRow from '@/epos/vue/modals/Settings/components/KitchenZonePrintersRow';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import { ConfirmTabAwayMixin } from '@/vue/mixins/ConfirmTabAwayMixin';
import { container } from '@/container';

export default {

    components: { KitchenZonePrintersRow, PageLoadingSpinner },

    mixins: [
        ConfirmTabAwayMixin,
    ],

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            /**
             * List of all printer names for the selects.
             *
             * @type {string[]}
             */
            printerNames: [],

            // Properties of the modal.
            error: null,
            loading: true,
            saving: false,

            kitchenZones: null,

            originalPrintConfig: null,
            newPrintConfig: null,
        };
    },

    computed: {
        /**
         * @returns {Device}
         */
        currentDevice() {
            return container.currentDevice.getDevice();
        },
    },

    async created() {
        this.loading = true;

        try {
            // Load available printers.
            this.printerNames = await container.availablePrintersManager.getPrinterNames();

            // Parse the current printConfig from the device.
            if (this.currentDevice.printConfig) {
                this.originalPrintConfig = JSON.parse(this.currentDevice.printConfig);
            } else {
                this.originalPrintConfig = {};
            }

            // Create a copy of the print config to work with.
            this.newPrintConfig = { ...this.originalPrintConfig };
            if (!this.newPrintConfig.hasOwnProperty('kitchenZones')) {
                this.newPrintConfig.kitchenZones = {};
            }

            // Load kitchen zones from IndexedDB.
            this.kitchenZones = await container.kitchenZoneRepository.findForBranch(
                container.userService.currentBranch.ID
            );

            // Ensure each kitchen zone is present in the config.
            this.kitchenZones.forEach((kitchenZone) => {
                if (!this.newPrintConfig.kitchenZones.hasOwnProperty(kitchenZone.ID)) {
                    // Add this zone to the config if it's not already there.
                    this.newPrintConfig.kitchenZones[kitchenZone.ID] = [
                        null, // null means print on the default printer.
                    ];
                }
            });
        } catch (e) {
            this.error = e.message;
        } finally {
            this.loading = false;
        }
    },

    methods: {
        submit() {
            if (this.saving) {
                return;
            }

            this.error = null;
            this.saving = true;

            container.currentDevice.updatePrintConfigForCurrentDevice(this.newPrintConfig)
                .then(() => {
                    this.$emit('resolve');
                })
                .catch((e) => {
                    this.error = e.message;
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        /**
         * This is checked by the ConfirmTabAwayMixin to see there are unsaved changes when leaving the tab.
         *
         * @returns {boolean}
         */
        hasUnsavedChanges() {
            return JSON.stringify(this.originalPrintConfig) !== JSON.stringify(this.newPrintConfig);
        },
    },
};
</script>
